"use client"

import { API_BASE } from "@/constants/base-url"
import { apiCall } from "@/lib/api"

export type PendingQotdAttempt = {
  answeredAt: string // ISO timestamp
  correctAnswerText?: string
  expiresAt: string // ISO timestamp
  isCorrect: boolean
  questionId: string
  questionSlug: string
  selectedAnswerText?: string
  subjectSlug: string
  timeSeconds?: number
  version: 1
}

const STORAGE_PREFIX = "qotd-pending:"

export function clearPendingQotdAttemptByKey(key: string) {
  try {
    if (typeof window === "undefined") return
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export async function flushPendingQotdAttempts(keys?: string[]): Promise<
  Array<{
    attempt?: PendingQotdAttempt
    key: string
    success: boolean
  }>
> {
  if (typeof window === "undefined") return []
  const items = (
    keys && keys.length > 0
      ? keys
          .map((key) => {
            try {
              const raw = localStorage.getItem(key)
              if (!raw) return null
              const attempt = JSON.parse(raw) as PendingQotdAttempt
              return { attempt, key }
            } catch {
              return null
            }
          })
          .filter((x): x is { attempt: PendingQotdAttempt; key: string } =>
            Boolean(x && x.attempt)
          )
      : getPendingQotdAttempts()
  ).slice()

  const results: Array<{
    attempt?: PendingQotdAttempt
    key: string
    success: boolean
  }> = []

  for (const item of items) {
    const { attempt, key } = item
    try {
      const response = await apiCall<{ success: boolean }>(
        `${API_BASE}/qotd/attempts`,
        {
          body: JSON.stringify({
            correctAnswerText: attempt.correctAnswerText,
            isCorrect: attempt.isCorrect,
            questionId: attempt.questionId,
            questionSlug: attempt.questionSlug,
            selectedAnswerText: attempt.selectedAnswerText,
            subjectSlug: attempt.subjectSlug,
            timeSeconds: attempt.timeSeconds
          }),
          method: "POST"
        }
      )
      if (response.success) {
        clearPendingQotdAttemptByKey(key)
        results.push({ attempt, key, success: true })
      } else {
        results.push({ attempt, key, success: false })
      }
    } catch {
      results.push({ attempt, key, success: false })
    }
  }

  return results
}

export function getPendingQotdAttempts(): Array<{
  attempt: PendingQotdAttempt
  key: string
}> {
  const results: Array<{ attempt: PendingQotdAttempt; key: string }> = []
  try {
    if (typeof window === "undefined") return results
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || ""
      if (!key.startsWith(STORAGE_PREFIX)) continue
      const raw = localStorage.getItem(key)
      if (!raw) continue
      try {
        const parsed = JSON.parse(raw) as PendingQotdAttempt
        if (
          parsed &&
          parsed.version === 1 &&
          typeof parsed.subjectSlug === "string" &&
          typeof parsed.questionId === "string"
        ) {
          const now = Date.now()
          const exp = Date.parse(parsed.expiresAt || "")
          if (!Number.isNaN(exp) && exp > now) {
            results.push({ attempt: parsed, key })
          } else {
            // expired, clean up
            localStorage.removeItem(key)
          }
        }
      } catch {
        // bad JSON, clean up
        localStorage.removeItem(key)
      }
    }
  } catch {
    // ignore storage errors
  }
  return results
}

export function queuePendingQotdAttempt(
  attempt: Omit<PendingQotdAttempt, "answeredAt" | "expiresAt" | "version"> & {
    answeredAt?: string
  }
) {
  try {
    if (typeof window === "undefined") return
    const now = new Date()
    const answeredAt = attempt.answeredAt || now.toISOString()
    const attemptedDate = getUtcDateString(new Date(answeredAt))
    const expiresAt = new Date(
      now.getTime() + 24 * 60 * 60 * 1000
    ).toISOString()

    const payload: PendingQotdAttempt = {
      ...attempt,
      answeredAt,
      expiresAt,
      version: 1
    }

    const key = buildKey(attempt.subjectSlug, attemptedDate)
    localStorage.setItem(key, JSON.stringify(payload))
  } catch {
    // ignore storage errors
  }
}

function buildKey(subjectSlug: string, attemptedDate: string): string {
  return `${STORAGE_PREFIX}${subjectSlug}:${attemptedDate}`
}

function getUtcDateString(date: Date): string {
  return date.toISOString().slice(0, 10)
}
